import { ApiResponse } from '../../models/ApiResponse'
import {
  PickingRouteNextPositionDto,
  PickingRoute,
  PickingRouteDetails,
  PickingRouteMovementDto,
  UserWms,
  EmptyPickingRouteRequestDto,
  SaveDifferencesDto,
  StartPickingRoute
} from '../../interfaces/Picking/Picking'
import { apiCall, ApiConfig } from '../../config/api'
import { PickingDiff } from '../../interfaces/PickingValidate/PickingDiff'
import { TicketInfo } from '../../interfaces/PickingValidate/TicketInfo'
import {
  InventoryIncidentCreateDto,
  InventoryIncidentFilterDto,
  InventoryIncidentInfoDto,
  InventoryIncidentUpdateDto
} from '../../interfaces/PickingValidate/InventoryIncident'
import { AxiosError } from 'axios'
import { IPageResultModel } from '../../interfaces/DataGrid/IPageResultModel'
import { PositionInfo } from '../../interfaces/Position/Position'
import { ApiResponse as ApiPageResult } from '../../typings/ApiResponse'
import { eTypePickingRoute } from '../../enums/eTypePickingRoute'

export class PickingService {
  constructor(readonly callbackError: { (error: string): void }) {}

  private isAxiosError(candidate: any): candidate is AxiosError {
    return candidate.isAxiosError === true
  }

  public async getWmsUser(userEmail: string): Promise<UserWms | undefined> {
    const url = `${ApiConfig.UserApi}/ByEmail?email=${encodeURIComponent(
      userEmail
    )}`
    try {
      const axiosResponse = await apiCall('GET', url)
      return axiosResponse.data
    } catch (error) {
      if (this.isAxiosError(error) && error.response?.status === 404) {
        error = 'correo no registrado en WMS.'
      }
      this.processApiError('Error en consulta de usuario', error)
      return undefined
    }
  }

  public async startUserPicking(
    userId: number,
    typeRouteId: number = eTypePickingRoute.Estandar,
    zoneId: number
  ): Promise<PickingRoute | undefined> {
    const dto: StartPickingRoute = {
      typeRouteId: typeRouteId,
      userId: userId,
      zoneId: zoneId
    }

    const url = `${ApiConfig.PickingRouteApi}/StartUserPicking`
    try {
      let route: PickingRoute | undefined
      const axiosResponse = await apiCall('POST', url, dto)
      const apiResponse = axiosResponse.data as ApiResponse
      if (apiResponse.success) {
        route = apiResponse.data as PickingRoute
        route.createdAt = this.dateConvert(apiResponse.data.createdAt)
        route.startedAt = this.dateConvert(apiResponse.data.startedAt)
        route.endedAt = this.dateConvert(apiResponse.data.endedAt)
      } else {
        route = undefined
      }
      return route
    } catch (error) {
      this.processApiError('Error al iniciar nueva ruta de picking', error)
      return undefined
    }
  }

  public async getUserRoute(userId: number): Promise<PickingRoute | undefined> {
    const url = `${ApiConfig.PickingRouteApi}/GetUserRoute/${userId}`
    try {
      const axiosResponse = await apiCall('GET', url)
      const apiResponse = axiosResponse.data as ApiResponse
      let route: PickingRoute | undefined
      if (apiResponse.success) {
        route = apiResponse.data as PickingRoute
        route.createdAt = this.dateConvert(apiResponse.data.createdAt)
        route.startedAt = this.dateConvert(apiResponse.data.startedAt)
        route.endedAt = this.dateConvert(apiResponse.data.endedAt)
      }
      return route
    } catch (error) {
      this.processApiError('Error al consultar ruta de picking', error)
      return undefined
    }
  }

  public async getNextRoutePosition(
    dto: PickingRouteNextPositionDto
  ): Promise<PickingRouteDetails | undefined> {
    const url = `${ApiConfig.PickingRouteApi}/GetNextRoutePosition`
    try {
      let routeDetails: PickingRouteDetails | undefined
      const axiosResponse = await apiCall('POST', url, dto)
      const apiResponse = axiosResponse.data as ApiResponse
      if (apiResponse.success) {
        routeDetails = apiResponse.data as PickingRouteDetails
      } else {
        routeDetails = new PickingRouteDetails()
      }
      return routeDetails
    } catch (error) {
      this.processApiError('Error en consulta de ruta de picking', error)
      return undefined
    }
  }

  public async getPackingPositionByZone(
    zoneId: number,
    typePositionId: number
  ): Promise<PositionInfo[]> {
    const url = `${ApiConfig.PositionApi}?zoneId=${zoneId}&TypePositionId=${typePositionId}`

    try {
      let position: PositionInfo[] = []

      await apiCall('GET', url)
        .then((response) => {
          if (response.data.length !== 0) {
            const positionAux = response.data as ApiPageResult<PositionInfo>
            position = positionAux.items.filter((f) => f.active)
          }
        })
        .catch((error) => {
          this.processApiError('Error al consulta posiciones de caja', error)
          return position
        })

      return position
    } catch (error) {
      this.processApiError('Error al consulta posiciones de caja', error)
      return []
    }
  }

  public async getPackingPositionForRecallRoute(
    pickingRouteId: number
  ): Promise<PositionInfo[]> {
    const url = new URL(
      `${ApiConfig.PickingRouteApi}/${pickingRouteId}/positions`
    )

    try {
      let position: PositionInfo[] = []

      await apiCall('GET', url.toString())
        .then((response) => {
          if (response.data.length !== 0) {
            const positionAux = response.data as PositionInfo[]
            position = positionAux.filter((f) => f.active)
          }
        })
        .catch((error) => {
          this.processApiError('Error al consulta posiciones de caja', error)
          return position
        })

      return position
    } catch (error) {
      this.processApiError('Error al consulta posiciones de caja', error)
      return []
    }
  }

  public async savePickingRouteMovement(
    dto: PickingRouteMovementDto
  ): Promise<ApiResponse> {
    const url = `${ApiConfig.PickingRouteApi}/SavePickingRouteMovement`
    try {
      const axiosResponse = await apiCall('POST', url, dto)
      const apiResponse = axiosResponse.data as ApiResponse
      return apiResponse
    } catch (error) {
      this.processApiError('Error al guardar picking', error)
      return this.failApiResponse('' + error)
    }
  }

  public async emptyPickingRoute(
    dto: EmptyPickingRouteRequestDto
  ): Promise<ApiResponse> {
    const url = `${ApiConfig.PickingRouteApi}/EmptyPickingRoute`
    try {
      const axiosResponse = await apiCall('POST', url, dto)
      const apiResponse = axiosResponse.data as ApiResponse
      return apiResponse
    } catch (error) {
      this.processApiError('Error en operación de vaciado', error)
      return this.failApiResponse('' + error)
    }
  }

  public async getSkuPositions(
    pickingRouteId: number,
    skuId: number
  ): Promise<PickingDiff[] | undefined> {
    const url = `${ApiConfig.PickingRouteValidationApi}/GetMovementPickingRouteBySKU/${skuId}/${pickingRouteId}`
    try {
      let rows: PickingDiff[] = []
      const axiosResponse = await apiCall('GET', url)
      const apiResponse = axiosResponse.data as ApiPageResult<PickingDiff>
      if (axiosResponse.status) {
        const items = apiResponse.items
        rows = items.map(function (dto: any) {
          const row = new PickingDiff()
          Object.assign(row, dto)
          return row
        })
      } else {
        this.callbackError(
          `Error Obteniendo posiciones con stock para SKU ${skuId} en ruta ${pickingRouteId} `
        )
      }
      return rows
    } catch (error) {
      this.processApiError('Error al consultar validación de ruta', error)
      return undefined
    }
  }

  public async saveDifferences(
    payload: SaveDifferencesDto
  ): Promise<ApiResponse> {
    const url = `${ApiConfig.PickingRouteValidationApi}/SaveDifferences`
    try {
      const axiosResponse = await apiCall('POST', url, payload)
      const apiResponse = axiosResponse.data as ApiResponse
      return apiResponse
    } catch (error) {
      this.processApiError('Error al guardar diferencias', error)
      return this.failApiResponse('' + error)
    }
  }

  public async getPickingDetail(pickingRouteId: number): Promise<ApiResponse> {
    const url =
      `${ApiConfig.PickingRouteApi}/GetPickingDetail` +
      `?pickingRouteId=${pickingRouteId}`
    try {
      const axiosResponse = await apiCall('GET', url)
      const apiResponse = axiosResponse.data as ApiResponse
      return apiResponse
    } catch (error) {
      this.processApiError(
        'Error al consultar detalles de ruta de picking',
        error
      )
      return this.failApiResponse('' + error)
    }
  }

  public async getTicketInfo(
    pickingRouteId: number
  ): Promise<TicketInfo | undefined> {
    const url = `${ApiConfig.PickingRouteValidationApi}/GetTicketInfo/${pickingRouteId}`
    try {
      const axiosResponse = await apiCall('GET', url)
      return axiosResponse.data.data
    } catch (error) {
      this.processApiError('Error en consulta de ticket de ruta', error)
      return undefined
    }
  }

  public async getInventoryIncident(
    filtersDto: InventoryIncidentFilterDto
  ): Promise<InventoryIncidentInfoDto | undefined> {
    const filters: string[] = [
      `stockKeepingUnitId=${filtersDto.stockKeepingUnitId}`,
      `userId=${filtersDto.userId}`,
      `pickingRouteId=${filtersDto.pickingRouteId}`,
      `typeInventoryIncidentId=${filtersDto.typeInventoryIncidentId}`
    ]
    const strFilters = filters.join('&')
    const url = `${ApiConfig.InventoryIncident}/?${strFilters}`
    try {
      const axiosResponse = await apiCall('GET', url)
      const pageModel =
        axiosResponse.data as IPageResultModel<InventoryIncidentInfoDto>
      if (pageModel.totalItems > 0) return pageModel.items[0]
      else return undefined
    } catch (error) {
      if (this.isAxiosError(error) && error.response?.status === 404) {
        return undefined
      }
      this.processApiError('Error en consulta de usuario', error)
      return undefined
    }
  }

  public async createInventoryIncident(
    dto: InventoryIncidentCreateDto
  ): Promise<InventoryIncidentInfoDto | undefined> {
    const url = `${ApiConfig.InventoryIncident}`
    try {
      const axiosResponse = await apiCall('POST', url, dto)
      return axiosResponse.data
    } catch (error) {
      this.processApiError('Error en consulta de ruta de picking', error)
      return undefined
    }
  }

  public async updateInventoryIncident(
    dto: InventoryIncidentUpdateDto
  ): Promise<boolean> {
    const url = `${ApiConfig.InventoryIncident}/${dto.inventoryIncidentId}`
    try {
      await apiCall('PUT', url, dto)
      return true
    } catch (error) {
      this.processApiError('Error en consulta de ruta de picking', error)
      return false
    }
  }

  private processApiError(context: string, apiError: any) {
    let apiMessage: string = ''
    apiMessage = apiError.response?.data?.message || apiError
    const serviceMessage = `${context}: ${apiMessage}`
    this.callbackError(serviceMessage)
  }

  private dateConvert(value: any): Date | null {
    if (value) return new Date(value)
    else return null
  }

  private failApiResponse(message: string): ApiResponse {
    const apiResponse = new ApiResponse()
    apiResponse.success = false
    apiResponse.message = message
    return apiResponse
  }

  public async registerPositionAlert(
    positionId: number,
    skuId: number,
    userId: number,
    flowId: number,
    quantity: number,
    comments: string,
    batchId: number | undefined = undefined
  ): Promise<ApiResponse> {
    const url = `${ApiConfig.PositionAlert}`
    const dto = {
      positionId,
      userId,
      flowId,
      quantity,
      comments,
      skuId,
      batchId
    }
    try {
      const axiosResponse = await apiCall('POST', url, dto)
      const apiResponse = axiosResponse.data as ApiResponse
      return apiResponse
    } catch (error) {
      this.processApiError('Error al registrar alerta de posición', error)
      return this.failApiResponse('' + error)
    }
  }
}
